import React, { useState, useEffect } from "react";
import { Dialog } from "../containers/Dialog.tsx";
import { Button } from "../widgets/Button.tsx";
import { TextInput } from "../inputs/TextInput.tsx";
import { Select } from "../inputs/Select.tsx";
import { User } from "../../api/types.ts";
import { useDialog } from "../DialogManager.tsx";
import { usePatchProfile } from "../../api/hooks.ts";

interface ProfileDialogProps {
  open: boolean;
  closeDialog: () => void;
  profile: User | undefined;
}

const ProfileDialog: React.FC<ProfileDialogProps> = ({
  open,
  closeDialog,
  profile,
}) => {
  const { setError } = useDialog();

  const patchProfileMutation = usePatchProfile();

  const [name, setName] = useState<string>("");
  const [purpose, setPurpose] = useState<string>("");
  const [resistanceUnit, setResistanceUnit] = useState<"kg" | "lbs">("kg");
  const [timeFormat, setTimeFormat] = useState<"12h" | "24h">("24h");
  const [visibility, setVisibility] = useState<"private" | "friends">(
    "private",
  );
  const [initialized, setInitialized] = useState<boolean>(false);

  useEffect(() => {
    // Only initialize form once when profile is available
    if (profile && !initialized) {
      setName(profile.name || "");
      setPurpose(profile.purpose || "");
      setResistanceUnit(profile.preferred_resistance_unit || "");
      setTimeFormat(profile.preferred_time_format || "");
      setVisibility(profile.preferred_visibility || "");
      setInitialized(true);
    }
  }, [profile, initialized]);

  const profileIsIncomplete =
    !profile?.name ||
    !profile?.purpose ||
    !profile?.preferred_resistance_unit ||
    !profile?.preferred_time_format ||
    !profile?.preferred_visibility;
  const isValid =
    name.trim().length > 0 &&
    purpose.trim().length > 0 &&
    resistanceUnit &&
    timeFormat &&
    visibility;

  const handleSave = () => {
    const profileData: Partial<User> = {
      name: name.trim() || profile?.name,
      purpose: purpose.trim() as User["purpose"],
      preferred_resistance_unit: resistanceUnit,
      preferred_time_format: timeFormat,
      preferred_visibility: visibility,
    };

    patchProfileMutation.mutate(profileData, {
      onSuccess: closeDialog,
      onError: (e) => setError(e),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={closeDialog}
      title={profileIsIncomplete ? "Complete your profile" : "Edit Profile"}
      closable={!profileIsIncomplete}
    >
      <Dialog.Content>
        {profileIsIncomplete && (
          <p className="mb-4">
            We noticed your profile is incomplete. Please fill out the following
            information to enhance your experience. You can always update these
            settings later.
          </p>
        )}

        <TextInput
          label="Email"
          value={profile?.email || ""}
          disabled
          className="bg-gray-50 border-gray-50"
        />

        <TextInput
          label="Display Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          required
        />

        <Select
          label="What do you wish to accomplish?"
          value={purpose}
          onChange={(value) => setPurpose(value)}
          required
          options={[
            { id: "", label: "Select an option" },
            { id: "improve_fitness", label: "Improve Fitness" },
            { id: "maintain_fitness", label: "Maintain Fitness" },
            { id: "general_tracking", label: "General Tracking" },
            { id: "other", label: "Other" },
            { id: "prefer_not_to_say", label: "Prefer not to say" },
          ]}
        />

        <Select
          label="Preferred Resistance Unit"
          value={resistanceUnit}
          onChange={(value) => setResistanceUnit(value as "kg" | "lbs")}
          options={[
            { id: "", label: "Select an option" },
            { id: "kg", label: "Kilograms (kg)" },
            { id: "lbs", label: "Pounds (lbs)" },
          ]}
          required
        />

        <Select
          label="Preferred Time Format"
          value={timeFormat}
          onChange={(value) => setTimeFormat(value as "12h" | "24h")}
          options={[
            { id: "", label: "Select an option" },
            { id: "24h", label: "24-hour" },
            { id: "12h", label: "12-hour (AM/PM)" },
          ]}
          required
        />

        <Select
          label="Preferred Visibility"
          value={visibility}
          onChange={(value) => setVisibility(value as "private" | "friends")}
          options={[
            { id: "", label: "Select an option" },
            { id: "private", label: "Keep my workouts private" },
            {
              id: "friends",
              label: "Share my workouts to the activity feed",
            },
          ]}
          required
          infoText="The activity feed displays completed workouts between you and your
          friends. You set the visibility of each workout you record, defaulting
          to the visibility prefence set on your profile."
        />
      </Dialog.Content>
      <Dialog.Actions>
        <Button
          variant="submit"
          onClick={handleSave}
          disabled={!isValid || patchProfileMutation.isPending}
          loading={patchProfileMutation.isPending}
        >
          Save
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

export { ProfileDialog };
